import { useCallback, useEffect, useRef } from "react";

/**
 * useDialogA11y — contrato único de accesibilidad para los diálogos modales.
 *
 * Fuente única para SlidePanel, el modal de useConfirm y Modal. Sin esto cada
 * overlay reimplementaba (o se saltaba) las cuatro obligaciones de un diálogo
 * modal, y un usuario de teclado o lector de pantalla seguía "dentro" de la
 * página de atrás mientras un panel le tapaba la pantalla:
 *
 *   1. Anunciarse — role="dialog" + aria-modal + nombre accesible.
 *   2. Recibir el foco al abrir.
 *   3. Retener el foco mientras está abierto (Tab cicla dentro).
 *   4. Devolver el foco al elemento que lo abrió al cerrar.
 *
 * Escape se escucha a nivel documento (no en el contenedor) para no regresionar
 * el comportamiento previo: cierra aunque el foco se haya ido fuera del panel.
 *
 * @param {object}  opts
 * @param {boolean} opts.isOpen
 * @param {Function} [opts.onClose]        invocado con Escape
 * @param {string}  [opts.labelledBy]      id del título — preferido sobre `label`
 * @param {string}  [opts.label]           nombre accesible cuando no hay título visible
 * @param {import("react").RefObject<HTMLElement>} [opts.initialFocusRef]
 *   Qué enfocar al abrir. Por defecto el primer elemento enfocable del diálogo.
 *   En diálogos destructivos DEBE apuntar a la acción segura (Cancelar): dejar
 *   el foco sobre "Confirmar" convierte un Enter reflejo en un corte de
 *   combustible.
 *
 * @returns {{
 *   dialogRef: import("react").RefObject<HTMLElement>,
 *   dialogProps: object,
 * }}
 */

// `:not([tabindex='-1'])` deja fuera los contenedores enfocables por programa.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Enfocables visibles dentro de `root`.
 *
 * Deliberadamente NO se filtra por `offsetParent`/`getBoundingClientRect`: jsdom
 * no hace layout y los daría todos como ocultos, dejando la trampa de foco sin
 * cobertura de tests. `hidden` y `aria-hidden` sí son fiables en jsdom.
 */
function getFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true",
  );
}

export function useDialogA11y({
  isOpen,
  onClose,
  labelledBy,
  label,
  initialFocusRef,
} = {}) {
  const dialogRef = useRef(null);

  // Escape a nivel documento — mismo alcance que la implementación anterior.
  useEffect(() => {
    if (!isOpen || !onClose) return undefined;
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Foco de entrada + restauración al cerrar.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused =
      typeof document !== "undefined" ? document.activeElement : null;
    const node = dialogRef.current;
    const target =
      initialFocusRef?.current ?? getFocusable(node)[0] ?? node;
    target?.focus?.();

    return () => {
      // Solo se devuelve el foco si el origen sigue en el documento: si la
      // acción del diálogo desmontó la fila que lo abrió, forzar el foco ahí
      // lo mandaría a un nodo huérfano y el navegador lo tiraría al <body>.
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === "function" &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
    // `initialFocusRef` es un ref estable; no debe re-disparar el efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Trampa de foco: Tab cicla dentro del diálogo.
  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    const node = dialogRef.current;
    const items = getFocusable(node);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = node?.contains(active);

    if (e.shiftKey && (active === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  return {
    dialogRef,
    dialogProps: {
      role: "dialog",
      "aria-modal": "true",
      ...(labelledBy ? { "aria-labelledby": labelledBy } : {}),
      ...(!labelledBy && label ? { "aria-label": label } : {}),
      tabIndex: -1,
      onKeyDown: handleKeyDown,
    },
  };
}
